import React, { useMemo, useState } from 'react';
import { StatCard } from '../components/base';
import { baixarCSV, leadParaLinha } from '../lib/exportar';
import { db } from '../lib/firebase';
import { calcContractTotal, fmt } from '../lib/formato';

function ConcluidasView({ leads, sellers, onOpenLead }) {
  const [periodo, setPeriodo] = useState('90');
  const [modo, setModo] = useState('ganhas'); // ganhas | perdidas | ambas

  const dados = useMemo(() => {
    const dias = Number(periodo);
    const corte = dias > 0 ? Date.now() - dias * 86400000 : 0;
    // Ganha data pelo fechamento, perdida data pela perda. stage_changed_at não serve para
    // perdida: a promoção para repescagem o reescreve e a perda mudaria de mês sozinha.
    const quando = (l) => (l.stage === 'fechado' ? l.closed_at : l.lost_at)
                       || l.stage_changed_at || l.updated_at;
    const dentro = (l) => {
      if (!corte) return true;
      const d = quando(l);
      return d ? new Date(d).getTime() >= corte : false;
    };

    const ganhas   = leads.filter(l => l.stage === 'fechado' && dentro(l));
    // 'repescagem' entra junto: a perda aconteceu de verdade e o mês fechado não pode
    // perder uma perda só porque o lead voltou para a fila de retomada.
    const perdidas = leads.filter(l => ['perdido','repescagem'].includes(l.stage) && dentro(l));

    const soma = (arr, fn) => arr.reduce((s,l) => s + fn(l), 0);
    const receitaMensal   = soma(ganhas, l => Number(l.value) || 0);
    const receitaContrato = soma(ganhas, l => calcContractTotal(l.value, l.recurrence));
    const valorPerdido    = soma(perdidas, l => Number(l.value) || 0);

    // ciclo de venda: da criação até o fechamento
    const ciclos = ganhas.map(l => {
      const fim = l.closed_at || l.stage_changed_at || l.updated_at;
      if (!l.created_at || !fim) return null;
      return Math.max(0, Math.round((new Date(fim) - new Date(l.created_at)) / 86400000));
    }).filter(v => v !== null);
    const cicloMedio = ciclos.length ? ciclos.reduce((s,v)=>s+v,0) / ciclos.length : 0;

    const total = ganhas.length + perdidas.length;
    const winRate = total > 0 ? (ganhas.length / total) * 100 : 0;
    const ticket  = ganhas.length ? receitaContrato / ganhas.length : 0;

    // por vendedor
    const porVendedor = {};
    for (const l of ganhas) {
      const k = l.seller_name || 'Sem vendedor';
      porVendedor[k] ||= { nome:k, qtd:0, valor:0 };
      porVendedor[k].qtd++;
      porVendedor[k].valor += Number(l.value) || 0;
    }

    // motivos de perda
    const MOTIVOS = { sem_resposta:'Não respondeu', preco:'Preço', concorrente:'Concorrente', atendimento_ineficiente:'Atendimento ineficiente', outros:'Outros' };
    const porMotivo = {};
    for (const l of perdidas) {
      const k = MOTIVOS[l.loss_reason] || 'Não informado';
      porMotivo[k] = (porMotivo[k] || 0) + 1;
    }

    return {
      ganhas, perdidas, receitaMensal, receitaContrato, valorPerdido,
      cicloMedio, winRate, ticket,
      porVendedor: Object.values(porVendedor).sort((a,b)=>b.valor-a.valor),
      porMotivo: Object.entries(porMotivo).sort((a,b)=>b[1]-a[1]),
    };
  }, [leads, periodo]);

  const lista = modo === 'ganhas' ? dados.ganhas
              : modo === 'perdidas' ? dados.perdidas
              : [...dados.ganhas, ...dados.perdidas];

  const ordenada = [...lista].sort((a,b) => {
    const da = a.closed_at || a.stage_changed_at || a.updated_at || '';
    const db = b.closed_at || b.stage_changed_at || b.updated_at || '';
    return db.localeCompare(da);
  });

  return <div className="flex flex-col gap-5">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-lg font-bold text-gray-800">Negociações Concluídas</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <select className="apple-input px-3 py-2 text-sm" value={periodo} onChange={e=>setPeriodo(e.target.value)}>
          <option value="30">Últimos 30 dias</option>
          <option value="90">Últimos 90 dias</option>
          <option value="180">Últimos 6 meses</option>
          <option value="365">Último ano</option>
          <option value="0">Desde sempre</option>
        </select>
        <button onClick={()=>baixarCSV('negociacoes-concluidas', ordenada.map(leadParaLinha))}
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-colors whitespace-nowrap">
          <span aria-hidden="true">⬇</span> CSV
        </button>
      </div>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <StatCard label="Ganhas"          value={dados.ganhas.length} color="text-green-600" />
      <StatCard label="Perdidas"        value={dados.perdidas.length} color={dados.perdidas.length>0?'text-red-500':'text-gray-500'} />
      <StatCard label="Win Rate"        value={`${dados.winRate.toFixed(1)}%`} color={dados.winRate>=50?'text-green-600':'text-orange-500'} />
      <StatCard label="Receita Mensal"  value={fmt(dados.receitaMensal)} color="text-green-700" sub="soma do valor mensal" />
      <StatCard label="Ticket Médio"    value={dados.ticket>0?fmt(dados.ticket):'—'} color="text-teal-700" sub="por contrato" />
      <StatCard label="Ciclo Médio"     value={dados.cicloMedio>0?`${dados.cicloMedio.toFixed(0)}d`:'—'} sub="da criação ao fechamento" />
    </div>

    {dados.valorPerdido > 0 && (
      <div className="acard rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap">
        <span aria-hidden="true">💸</span>
        <span className="text-sm text-gray-600">
          Valor mensal deixado na mesa: <strong className="text-red-600">{fmt(dados.valorPerdido)}</strong>
        </span>
      </div>
    )}

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {dados.porVendedor.length > 0 && <div className="acard rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Fechamentos por vendedor</h3>
        <div className="space-y-2">
          {dados.porVendedor.map(v=>{
            const max = dados.porVendedor[0].valor || 1;
            return <div key={v.nome} className="flex items-center gap-3">
              <span className="text-xs text-gray-600 w-28 truncate">{v.nome}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{width:`${(v.valor/max)*100}%`}} />
              </div>
              <span className="text-xs font-semibold text-gray-700 w-8 text-right">{v.qtd}</span>
              <span className="text-xs text-green-700 font-medium w-24 text-right">{fmt(v.valor)}</span>
            </div>;
          })}
        </div>
      </div>}

      {dados.porMotivo.length > 0 && <div className="acard rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Por que perdemos</h3>
        <div className="space-y-2">
          {dados.porMotivo.map(([motivo,qtd])=>{
            const max = dados.porMotivo[0][1] || 1;
            return <div key={motivo} className="flex items-center gap-3">
              <span className="text-xs text-gray-600 w-32 truncate">{motivo}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-red-400 rounded-full" style={{width:`${(qtd/max)*100}%`}} />
              </div>
              <span className="text-xs font-semibold text-gray-700 w-8 text-right">{qtd}</span>
            </div>;
          })}
        </div>
      </div>}
    </div>

    <div className="acard rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-700">Detalhamento</h3>
        <div className="flex gap-0.5 bg-black/[0.05] rounded-full p-0.5">
          {[['ganhas','Ganhas'],['perdidas','Perdidas'],['ambas','Todas']].map(([k,lb])=>(
            <button key={k} onClick={()=>setModo(k)}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-all ${modo===k?'bg-white text-gray-900 shadow-sm':'text-gray-400 hover:text-gray-600'}`}>
              {lb}
            </button>
          ))}
        </div>
      </div>
      {ordenada.length === 0
        ? <p className="text-sm text-gray-400 text-center py-8">Nada no período escolhido.</p>
        : <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {ordenada.map(l=>{
              const ganho = l.stage === 'fechado';
              const quando = l.closed_at || l.stage_changed_at || l.updated_at;
              return <div key={l.id} onClick={()=>onOpenLead(l)}
                className="flex items-center justify-between gap-3 bg-black/[0.03] hover:bg-black/[0.06] rounded-xl px-3 py-2 cursor-pointer transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{l.company_name}</p>
                  <p className="t-caption-2 text-gray-400 truncate">
                    {l.seller_name || 'sem vendedor'}
                    {quando ? ` · ${new Date(quando).toLocaleDateString('pt-BR')}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {l.value > 0 && <span className={`text-xs font-bold ${ganho?'text-green-700':'text-gray-400'}`}>{fmt(l.value)}</span>}
                  <span className={`t-caption-2 px-2 py-0.5 rounded-full font-medium ${ganho?'bg-green-100 text-green-700':'bg-red-100 text-red-600'}`}>
                    {ganho?'Ganha':'Perdida'}
                  </span>
                </div>
              </div>;
            })}
          </div>}
    </div>
  </div>;
}

// ─── C-24: visão calendário ───────────────────────────────

export { ConcluidasView };
